import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/client";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import { getSignupSource } from "../../utils/signupSource";
import useAuthStore from "../../store/authStore";

function getPasswordStrength(password) {
  if (!password) return null;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  if (passed <= 1)
    return {
      label: "Weak",
      color: "bg-red-400",
      width: "w-1/4",
      textColor: "text-red-500",
    };
  if (passed === 2)
    return {
      label: "Fair",
      color: "bg-amber-400",
      width: "w-2/4",
      textColor: "text-amber-500",
    };
  if (passed === 3)
    return {
      label: "Good",
      color: "bg-blue-400",
      width: "w-3/4",
      textColor: "text-blue-500",
    };
  return {
    label: "Strong",
    color: "bg-green-400",
    width: "w-full",
    textColor: "text-green-500",
  };
}

function validatePassword(password) {
  if (password.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(password))
    return "Password must contain at least one uppercase letter";
  if (!/\d/.test(password)) return "Password must contain at least one number";
  return null;
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get("next");
  const setSeekerAuth = useAuthStore((s) => s.setSeekerAuth);
  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Frontend validation before hitting the API
    const pwError = validatePassword(form.password);
    if (pwError) {
      setError(pwError);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Send `next` in the registration body so the backend can encode
      // it into the verification email link. This survives cross-browser
      // / cross-device verification (which sessionStorage cannot).
      const payload = {
        ...form,
        ...(nextUrl ? { next: nextUrl } : {}),
        // Where this visitor entered the site, captured on first page load
        ...getSignupSource(),
      };
      await api.post("/auth/register", payload);
      navigate("/verify-pending?email=" + encodeURIComponent(form.email));
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(form.password);
  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";

  return (
    <div className="min-h-screen bg-brand-light-bg dark:bg-brand-dark-bg flex flex-col">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
        <Logo className="h-12" />
        <ThemeToggle />
      </nav>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-white/10 p-8 w-full max-w-sm">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            Create your account
          </h1>
          <p className="text-gray-400 text-sm mb-6">
            Start tracking your job search for free
          </p>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <input
              name="full_name"
              type="text"
              placeholder="Full name"
              required
              value={form.full_name}
              onChange={handleChange}
              className={inputClass}
            />

            <input
              name="email"
              type="email"
              placeholder="Email"
              required
              value={form.email}
              onChange={handleChange}
              className={inputClass}
            />

            <div>
              <input
                name="password"
                type="password"
                placeholder="Password"
                required
                value={form.password}
                onChange={handleChange}
                className={inputClass}
              />

              {/* Password strength indicator */}
              {form.password && (
                <div className="mt-2">
                  <div className="h-1 bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className={`text-xs font-medium ${strength.textColor}`}>
                      {strength.label}
                    </p>
                    <p className="text-xs text-gray-400">
                      {!/[A-Z]/.test(form.password) && "Add uppercase · "}
                      {!/\d/.test(form.password) && "Add number · "}
                      {!/[^A-Za-z0-9]/.test(form.password) && "Add symbol"}
                    </p>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1.5">
                Min 8 characters, one uppercase letter and one number
              </p>
            </div>

            <p className="text-xs text-gray-400 leading-relaxed">
              By creating an account you agree to our{" "}
              <Link to="/terms" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-brand-violet dark:text-brand-violet-on-dark hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-violet text-white py-2.5 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? "Creating account…" : "Create account"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 mt-6">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-brand-violet dark:text-brand-violet-on-dark font-medium hover:underline"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
